import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useT } from "@transifex/react";
import type { PreviousUrlNavigationProps, StudentQuestionAnswer, StudentTestAnswer } from "p6m-p6u";
import LoadingSpinner from "../../basic/loadingSpinner/LoadingSpinner";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import ToastMessage from "../../basic/toastMessage/ToastMessage";
import { useGeneralContext } from "../../context/AppGeneralContext";
import { useStudentPracticeContext } from "../../context/StudentPracticeContext";
import { useTestCreationContext } from "../../context/TestCreationContext";
import { useClassMember } from "../../context/ClassMemberContext";
import { LoginErrorVariants, loginErrorVariantFromError } from "../../context/classMemberLoginErrors";
import { clearCodeReminder, isCodeReminderDue } from "../../context/classMemberStorage";
import { ExerciseTypes, Exercises } from "../../enums/exercises";
import { ShareModes } from "../../enums/shareFlow";
import { ToastVariants } from "../../enums/toast";
import { Views } from "../../enums/views";
import { getIsExampleExercise } from "../../helpers/Environment";
import {
    StudentTestAccessStates,
    isActivityBlockedForStudent,
    resolveStudentTestAccess,
} from "../../helpers/OnlineTestHelper";
import { buildStudentTestAnswer } from "../../helpers/StudentAnswerInitializer";
import { getIsLocalTestRun, getIsTestRun } from "../../helpers/Url";
import { getTestResultForClassMember, submitStudentTestResult } from "../../networking/tests";
import { hasClassMemberSubmitted } from "../../helpers/ClassMemberActivities";
import { getClassroomLoginRoute, getClassroomRoute } from "../../helpers/schoolClassHelpers";
import useBreakpoints from "../../hooks/useBreakpoints";
import useCountdown from "../../hooks/useCountdown";
import useExerciseNavigation from "../../hooks/useExerciseNavigation";
import { useClassDisplayInfo } from "../../hooks/useClassDisplayInfo";
import useExistingTestResult, { ExistingTestResultStates } from "../../hooks/useExistingTestResult";
import useToast from "../../hooks/useToast";
import StandardExercise from "./exercises/StandardExercise";
import ConnectWordsExercise from "./exercises/ConnectWordsExercise";
import GapSentenceExercise from "./exercises/GapSentenceExercise";
import WriteSentenceExercise from "./exercises/WriteSentenceExercise";
import CreateTextExercise from "./exercises/CreateTextExercise";
import FreeTextExercise from "./exercises/FreeTextExercise";
import ExercisesOverview from "./ExercisesOverview";
import { AreaButton, ButtonVariant } from "../teacherArea/AreaButton";
import OnlineTestIntro from "./onlineTestIntro/OnlineTestIntro";
import OnlineTestOutro from "./onlineTestOutro/OnlineTestOutro";
import OnlineTestLayout from "./layout/OnlineTestLayout";
import LegalFooter from "../legalFooter/LegalFooter";
import ClassMemberEntry from "../classroomLogin/ClassMemberEntry";
import ClassMemberJoinScreens from "../classroomLogin/ClassMemberJoinScreens";
import { ClassMemberJoinSteps, useClassMemberJoin } from "../classroomLogin/useClassMemberJoin";
import OnlineTestPageNavigation from "./layout/OnlineTestPageNavigation";
import OnlineTestStudentName from "./layout/OnlineTestStudentName";
import OnlineTestError from "./onlineTestError/OnlineTestError";
import { LoadFailureKinds, OnlineTestPhases } from "./onlineTestMachine";
import useOnlineTestMachine from "./useOnlineTestMachine";

const ExerciseComponentMap = new Map([
    [ExerciseTypes.get(Exercises.STANDARD), StandardExercise],
    [ExerciseTypes.get(Exercises.CONNECT_WORDS), ConnectWordsExercise],
    [ExerciseTypes.get(Exercises.FILL_GAP), GapSentenceExercise],
    [ExerciseTypes.get(Exercises.WRITE_SENTENCE), WriteSentenceExercise],
    [ExerciseTypes.get(Exercises.CREATE_TEXT), CreateTextExercise],
    [ExerciseTypes.get(Exercises.FREE_TEXT), FreeTextExercise],
]);

const OnlineTest = () => {
    const t = useT();
    const { testId } = useParams<{ testId: string }>();
    const history = useHistory();
    const location = useLocation<PreviousUrlNavigationProps>();
    const { state } = location;

    const t_backToClassroom = t("Back to classroom", { _tags: "OnlineTest" });
    const t_loading = t("Loading…", { _tags: "OnlineTest" });
    // Covers both load requests: the student cannot tell which failed, and the action is the same.
    const t_loadError = t("We couldn't load this page. Please check your connection and try again.", {
        _tags: "OnlineTest",
    });
    const t_retry = t("Try again", { _tags: "OnlineTest" });
    const t_submitError = t("Couldn't send your answers. Please try again.", { _tags: "OnlineTest" });

    const {
        teacherTest,
        studentTestAnswer,
        setStudentTestAnswer,
        currentMainView,
        setCurrentMainView,
        isPreview,
        setIsPreview,
        isOwner,
        setIsOwner,
        refreshData,
    } = useStudentPracticeContext();

    const { isAboveMD } = useBreakpoints();

    const { userInfo, userId, getAndSetUserAndTeacherInfo } = useGeneralContext();
    const { userTests, dataLoaded, userTestsLoadError, loadUserTests } = useTestCreationContext();

    const { member, isRestoringSession, login, logout } = useClassMember();

    const [countdownStarted, setCountdownStarted] = useState(false);
    const [countdownStartedAt, setCountdownStartedAt] = useState(0);
    const [testEndTimestamp, setTestEndTimestamp] = useState(0);
    const [detectedExistingResult, setDetectedExistingResult] = useState(false);
    const [loginError, setLoginError] = useState(LoginErrorVariants.NONE);
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const submitErrorToast = useToast();

    const hasSubmittedRef = useRef(false);
    const finishPracticeRef = useRef<() => void>(() => {});
    const scrollRef = useRef<HTMLDivElement>(null);
    const outroScrollRef = useRef<HTMLDivElement>(null);
    const authTriggeredRef = useRef(false);

    const {
        currentExerciseId,
        setCurrentExerciseId,
        currentExerciseIndex,
        currentPage,
        totalPages,
        showOverview,
        setShowOverview,
        handlePreviousPage,
        handleNextPage,
        handleOverviewClick,
        handleCloseOverview,
        handleNavigateToExercise,
    } = useExerciseNavigation({ content: teacherTest.content });

    const isSharedRecord = !!testId;

    // Owns the load sequence. `access` goes back in below, since the hooks deciding it consume these facts.
    const {
        state: machineState,
        identity: machineIdentity,
        schoolClassId,
        isLegacyActivity,
        isPrivateClass,
        isPrivateAccess,
        privateAccessMember,
        reportAccess,
        rehydrated,
        restoredSubmittedState,
        clearPersistedAnswers,
    } = useOnlineTestMachine({
        testId,
        teacherTest,
        hasTestRecord: !!teacherTest.id,
        isPreview,
        isOwner,
        member,
        isRestoringSession,
        userInfo,
        userId,
        isUserTestsLoaded: dataLoaded,
        hasUserTestsLoadError: userTestsLoadError,
        isTestOwner: userTests.some((t) => t.id === testId),
        loadUserTests,
        refreshData,
        studentTestAnswer,
        currentMainView,
        currentExerciseId,
        countdownStartedAt,
        setStudentTestAnswer,
        setCurrentMainView,
        setCurrentExerciseId,
        setCountdownStarted,
        setCountdownStartedAt,
        setTestEndTimestamp,
    });

    const isPractice = teacherTest.shareMode === ShareModes.PRACTICE;

    const isLoginRequired = machineState.phase === OnlineTestPhases.LOGIN_REQUIRED;

    const join = useClassMemberJoin({ schoolClassId: schoolClassId ?? "", login });
    const joinInfo = useClassDisplayInfo(isLoginRequired ? schoolClassId ?? "" : "");
    const isOnWelcome = join.step === ClassMemberJoinSteps.WELCOME;

    const existingResultState = useExistingTestResult({
        testId,
        shareMode: teacherTest.shareMode,
        classMemberId: privateAccessMember?.id ?? null,
        isClassMemberSessionReady: !!machineIdentity,
        isPreview,
    });

    // Re-checked rather than trusted from the intro: a persisted EXERCISES view rehydrates past its gate.
    const isBlockedByVisibility = isActivityBlockedForStudent({
        visibilityDate: teacherTest.visibilityDate,
        isLegacy: isLegacyActivity,
        // Both flags sourced as `needsPrivateLogin` explains below.
        isOwner: !!machineIdentity?.isOwner,
        // No share record, no visibility to enforce.
        isPreview: isPreview || !isSharedRecord,
    });

    const access = resolveStudentTestAccess({
        isResultCheckPending: existingResultState === ExistingTestResultStates.PENDING,
        // Three signals for the same fact: the load-time server lookup, a restored OUTRO from
        // localStorage (survives a backend hiccup), and a result found by the pre-submit check.
        hasAlreadySubmitted:
            existingResultState === ExistingTestResultStates.SUBMITTED ||
            restoredSubmittedState ||
            detectedExistingResult,
        isBlockedByVisibility,
    });

    /*
     * The two flags come from different places on purpose, each being the fresher one.
     *
     * Ownership off `machineIdentity`: the mirror effect below copies it a render later, and the redirect
     * wins that race — a teacher opening their own private-class test would be sent to the member login.
     *
     * `isPreview` off the context: TestPreview sets it from a mount effect that runs after this child's,
     * so the machine's snapshot can be stale.
     */
    const needsPrivateLogin =
        !!machineIdentity && !isPreview && !machineIdentity.isOwner && isPrivateClass && !isPrivateAccess;

    // `machineState.phase` belongs in the deps: the answer is usually settled before the machine can
    // accept it, and an early one is dropped — without it the machine never leaves the spinner.
    useEffect(() => {
        reportAccess(access, needsPrivateLogin);
    }, [reportAccess, access, needsPrivateLogin, machineState.phase]);

    const { isTimeOver, timeRemaining, isTimeRunningOut, shouldBounceTimer, clearBounce } = useCountdown({
        rehydrated,
        teacherTest,
        currentMainView,
        countdownStarted,
        setCountdownStarted,
        testEndTimestamp,
        setTestEndTimestamp,
        setCountdownStartedAt,
        isPreview,
        onTimeOver: () => finishPracticeRef.current(),
    });

    const [studentName, setStudentName] = useState(studentTestAnswer.studentName || "");

    /*
     * Private classes have a logged-in member, legacy tests only the typed name, public classes none.
     * Previews are excluded because buildStudentTestAnswer seeds studentName with the *teacher's*.
     */
    const displayedStudentName = (() => {
        if (isOwner || isPreview) return undefined;
        if (privateAccessMember?.name) return privateAccessMember.name;
        return isLegacyActivity ? studentName.trim() || undefined : undefined;
    })();

    // No room for name and countdown on one mobile row, so the name moves to the sub header row and
    // the countdown stays in view. Without a countdown the name takes its place in the header instead.
    const isNameBelowHeader = !!displayedStudentName && !isAboveMD && !!timeRemaining;

    const updateStudentName = (name: string) => {
        const newSa: StudentTestAnswer = { ...studentTestAnswer };
        newSa.studentName = name;
        setStudentTestAnswer(newSa);
    };

    const updateStudentAnswerOnQuestion = (newStudentAnswerItem: StudentQuestionAnswer, exerciseId?: string) => {
        const newStudentAnswerObj = { ...studentTestAnswer };
        if (!newStudentAnswerObj.questionAnswers) return;
        const targetId = exerciseId ?? currentExerciseId;
        newStudentAnswerObj.questionAnswers[targetId] = newStudentAnswerItem;
        setStudentTestAnswer(newStudentAnswerObj);
    };

    const saveData = useCallback(async () => {
        const sentTime = Date.now();
        const testIdVal = teacherTest.id!;
        const testResultToStore: StudentTestAnswer = {
            ...studentTestAnswer,
            isTimeOver,
            sentTime,
            ...(privateAccessMember ? { classMemberId: privateAccessMember.id } : {}),
        };
        await submitStudentTestResult(testIdVal, testResultToStore, { isPreview, isOwner });
    }, [isTimeOver, teacherTest.id, studentTestAnswer, privateAccessMember, isPreview, isOwner]);

    const navigateToClassroom = () => {
        if (!schoolClassId) return;
        history.push(getClassroomRoute(schoolClassId));
    };

    const handleStartJoin = () => {
        setLoginError(LoginErrorVariants.NONE);
        join.startJoin();
    };

    // The machine notices the new member on its own (IDENTITY_CHANGED), so success needs no handling here.
    const handleEntryLogin = async (classCode: string, personalCode: string) => {
        setIsLoggingIn(true);
        setLoginError(LoginErrorVariants.NONE);
        try {
            await login(classCode, personalCode);
        } catch (e) {
            setLoginError(loginErrorVariantFromError(e));
        } finally {
            setIsLoggingIn(false);
        }
    };

    const handleSwitchAccount = () => {
        if (!schoolClassId) return;
        logout();
        history.replace(getClassroomLoginRoute(schoolClassId));
    };

    // A reload rather than one request: the test fetch has no retry of its own, so retrying only the
    // class would be arbitrary on the flaky connection that caused this.
    const retryLoad = () => {
        window.location.reload();
    };

    const finishPractice = async () => {
        if (hasSubmittedRef.current) return;
        // Preview never submits, even when the countdown expires. Route to the close action so the
        // owner doesn't flash through an OUTRO before the reset effect catches it.
        if (isPreview) {
            hasSubmittedRef.current = true;
            handleClosePreview();
            return;
        }

        // Don't fall through to the pre-submit GET, which fails open and would POST a duplicate on error.
        // Terminal, unlike the two states below, so latching is safe here.
        if (access === StudentTestAccessStates.ALREADY_SUBMITTED) {
            hasSubmittedRef.current = true;
            return;
        }

        // Recoverable — the teacher may re-show the test — so this must not latch `hasSubmittedRef`:
        // that is permanent and would kill every later submit.
        if (isBlockedByVisibility) return;

        if (isPractice) {
            // Practice is repeatable, so the OUTRO copy ("answers sent to your teacher") would be wrong
            // and persisting it would trap them on the next visit. Back to the classroom, or the intro.
            clearPersistedAnswers();
            if (schoolClassId) {
                hasSubmittedRef.current = true;
                navigateToClassroom();
            } else {
                // isPreview is false here — the preview branch returned above.
                const isTestRun = !!getIsTestRun() || !!getIsLocalTestRun();
                const { studentTestAnswer: freshAnswer } = buildStudentTestAnswer(teacherTest, isTestRun);
                setStudentTestAnswer(freshAnswer);
                setCurrentExerciseId("e1");
                setShowOverview(false);
                setCurrentMainView(Views.INTRO);
            }
            return;
        }

        hasSubmittedRef.current = true;

        if (!isPreview && !getIsTestRun() && !getIsExampleExercise() && !getIsLocalTestRun()) {
            // Re-checked immediately before POSTing: a second tab or later session may have submitted
            // since. Fails open — a failed check must not cost the student the answers they just finished.
            if (privateAccessMember && teacherTest.shareMode === ShareModes.TEST && teacherTest.id) {
                try {
                    const existing = await getTestResultForClassMember(teacherTest.id, privateAccessMember.id);
                    if (hasClassMemberSubmitted({ shareMode: ShareModes.TEST }, existing)) {
                        clearPersistedAnswers();
                        setDetectedExistingResult(true);
                        return;
                    }
                } catch (e) {
                    console.warn("Pre-submit result check failed, submitting anyway", e);
                }
            }

            try {
                await saveData();
            } catch (e) {
                console.error(e);
                hasSubmittedRef.current = false;
                submitErrorToast.triggerToast();
                return;
            }
        }
        setCurrentMainView(Views.OUTRO);
    };

    finishPracticeRef.current = finishPractice;

    useEffect(() => {
        if (!rehydrated) return;
        if (Object.keys(studentTestAnswer.questionAnswers || {}).length > 0 || !teacherTest.content) return;

        const isPreviewOrTestRun = isPreview || !!getIsTestRun() || !!getIsLocalTestRun();
        const { studentTestAnswer: newAnswer } = buildStudentTestAnswer(teacherTest, isPreviewOrTestRun);
        setStudentTestAnswer(newAnswer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teacherTest, rehydrated]);

    useEffect(() => {
        if (studentTestAnswer.studentName) {
            setStudentName(studentTestAnswer.studentName);
        }
    }, [studentTestAnswer.studentName]);

    useEffect(() => {
        scrollRef.current?.scrollTo(0, 0);
    }, [currentExerciseId]);

    // Only covers `/student/` with no id: a bad URL, rather than a load that can fail.
    useEffect(() => {
        if (isPreview || testId) return;
        setCurrentMainView(Views.UNAUTHORIZED);
    }, [testId, setCurrentMainView, isPreview]);

    // /student/:id is not wrapped by an auth-bootstrapping route (unlike /assistent), so a teacher
    // landing on the share link directly never has their session resolved unless we trigger it
    // here. Anonymous students fall through harmlessly — the call sets an anonymous user and
    // leaves userInfo.isAnonymousUser=true.
    useEffect(() => {
        if (authTriggeredRef.current) return;
        if (!testId || isPreview) return;
        if (userInfo?.visitorOrUserRole || userInfo?.isAnonymousUser) return;
        authTriggeredRef.current = true;
        getAndSetUserAndTeacherInfo();
    }, [testId, isPreview, userInfo, getAndSetUserAndTeacherInfo]);

    /*
     * Mirrors the ownership answer onto StudentPracticeContext, which the intro, countdown and submit
     * guard read. A teacher opening their own test enters preview rather than submitting as a student —
     * even while holding a member session for that class ("Student view" holds both).
     *
     * Only ever flips these on: ownership is answered once, so a mid-flow login cannot create an owner.
     */
    useEffect(() => {
        if (!machineIdentity?.isOwner) return;
        setIsOwner(true);
        setIsPreview(true);
    }, [machineIdentity?.isOwner, setIsOwner, setIsPreview]);

    /*
     * A session for another class needs no cleanup here: every consumer of `member` is gated on
     * `isPrivateAccess`, false for a public class, so a non-matching session is already inert. Destroying
     * it logs the student out of their real classroom in every other tab, and re-logging in there
     * re-triggers the discard — the endless loop of HALLOFUSS-6670.
     */

    // Preview never shows the OUTRO — owners can't submit, so a "done!" screen has no meaning. A stored
    // OUTRO from an earlier student session can still rehydrate one, so route back to the route's entry
    // view: INTRO on /student/:id, EXERCISES on /create/preview.
    useEffect(() => {
        if (!isPreview) return;
        if (currentMainView !== Views.OUTRO) return;
        setCurrentMainView(testId ? Views.INTRO : Views.EXERCISES);
    }, [isPreview, currentMainView, testId, setCurrentMainView]);

    const handleStart = () => {
        setCurrentMainView(Views.EXERCISES);
        if (privateAccessMember?.name) {
            updateStudentName(privateAccessMember.name);
        } else {
            updateStudentName(studentName);
        }
    };

    const handleSubmit = () => {
        setShowOverview(false);
        finishPractice();
    };

    const handleClosePreview = useCallback(() => {
        // /student/:id route → testId present → owner returns to /assistent. /create/preview route
        // (no testId param) → in-flow author returns to /create. urlBeforePreview wins either way.
        const fallback = testId ? "/assistent" : "/create";
        const targetUrl = state?.urlBeforePreview || fallback;
        history.replace(targetUrl);
    }, [history, state, testId]);

    const renderExercise = () => {
        if (!teacherTest.content || currentExerciseIndex === -1) return null;

        const exercise = teacherTest.content[currentExerciseIndex];
        const exerciseId = exercise.id;
        if (!exerciseId) return null;

        const studentAnswer = studentTestAnswer.questionAnswers?.[exerciseId];
        if (!studentAnswer) return null;

        if (!exercise.questionMode) return null;
        const ExerciseComponent = ExerciseComponentMap.get(exercise.questionMode) ?? StandardExercise;

        return (
            <ExerciseComponent
                key={exerciseId}
                exercise={exercise}
                exerciseIndex={currentExerciseIndex}
                studentAnswer={studentAnswer}
                onAnswerUpdate={updateStudentAnswerOnQuestion}
            />
        );
    };

    const renderBackToClassroomLink = () => {
        if (!schoolClassId) return null;

        return (
            <button
                type="button"
                onClick={navigateToClassroom}
                className="flex items-center gap-xs bg-transparent border-none cursor-pointer p-no"
                data-testid="online-test-back-to-classroom"
            >
                <PhaseSixIcon
                    name="chevron-left"
                    className="text-[24px] text-p6-grey-dark"
                />
                <span className="text-l text-p6-grey-dark">{t_backToClassroom}</span>
            </button>
        );
    };

    const renderIntroView = () => (
        <OnlineTestLayout
            topBar={renderBackToClassroomLink()}
            footer={<LegalFooter />}
            footerScrolls
        >
            <OnlineTestIntro
                testName={teacherTest.name}
                // Trimmed because legacy records may hold whitespace-only comments.
                teacherMessage={teacherTest.teacherComment?.trim()}
                dueDate={teacherTest.dueDate}
                timeLimit={teacherTest.timeLimit}
                visibilityDate={teacherTest.visibilityDate}
                bookInfo={teacherTest.bookInfo?.name}
                studentName={studentName}
                onNameChange={setStudentName}
                onStart={handleStart}
                shareMode={teacherTest.shareMode}
                isLegacy={isLegacyActivity}
                classMemberName={!isOwner ? privateAccessMember?.name : undefined}
                onSwitchAccount={isPrivateAccess && !isOwner ? handleSwitchAccount : undefined}
                isOwner={isOwner}
                isPreview={isPreview}
            />
        </OnlineTestLayout>
    );

    const renderTestTitleHeader = () => {
        const sublineParts = [joinInfo?.className, joinInfo?.teacherDisplayName].filter(Boolean);
        return (
            <div className="flex w-full flex-col items-center gap-xs text-center">
                <h1 className="text-xxl font-semibold text-p6-grey-dark break-words">{teacherTest.name}</h1>
                {sublineParts.length > 0 && (
                    <p
                        className="text-m text-p6-grey-dark break-words"
                        data-testid="online-test-login-subline"
                    >
                        {sublineParts.join(" • ")}
                    </p>
                )}
            </div>
        );
    };

    const renderLoginRequiredView = () => (
        <OnlineTestLayout
            footer={<LegalFooter />}
            footerScrolls
        >
            <div
                className="flex flex-1 flex-col items-center pt-xxxxl pb-xxl w-full"
                data-testid="online-test-login-required"
            >
                <ClassMemberJoinScreens
                    join={join}
                    joinInfo={joinInfo}
                    entry={
                        <ClassMemberEntry
                            header={renderTestTitleHeader()}
                            classCode={schoolClassId ?? ""}
                            loginError={loginError}
                            isLoggingIn={isLoggingIn}
                            onLogin={handleEntryLogin}
                            onJoin={handleStartJoin}
                        />
                    }
                />
            </div>
        </OnlineTestLayout>
    );

    const renderExercisesOverview = () => (
        <ExercisesOverview
            exercises={teacherTest.content ?? []}
            questionAnswers={studentTestAnswer.questionAnswers ?? {}}
            timeRemaining={timeRemaining}
            isTimeRunningOut={isTimeRunningOut}
            shouldBounceTimer={shouldBounceTimer}
            onBounceEnd={clearBounce}
            studentName={displayedStudentName}
            currentExerciseId={currentExerciseId}
            currentExerciseNumber={currentPage}
            isPreview={isPreview}
            isOwner={isOwner}
            shareMode={teacherTest.shareMode}
            onClose={handleCloseOverview}
            onNavigateToExercise={handleNavigateToExercise}
            onSubmit={isPreview ? handleClosePreview : handleSubmit}
        />
    );

    const renderExercisesView = () => (
        <OnlineTestLayout
            showHeader
            headerTimeRemaining={timeRemaining}
            isTimeRunningOut={isTimeRunningOut}
            shouldBounceTimer={shouldBounceTimer}
            onBounceEnd={clearBounce}
            headerStudentName={isNameBelowHeader ? undefined : displayedStudentName}
            subHeader={
                isNameBelowHeader ? (
                    <OnlineTestStudentName
                        studentName={displayedStudentName}
                        size="text-m"
                    />
                ) : undefined
            }
            onOverviewClick={handleOverviewClick}
            scrollRef={scrollRef}
            footer={
                <OnlineTestPageNavigation
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPrevious={handlePreviousPage}
                    onNext={handleNextPage}
                />
            }
        >
            <div className="flex flex-col gap-l w-full">{renderExercise()}</div>
        </OnlineTestLayout>
    );

    const renderOutroView = (isAlreadySubmitted?: boolean) => (
        <OnlineTestLayout
            topBar={renderBackToClassroomLink()}
            footer={<LegalFooter />}
            footerScrolls
            scrollRef={outroScrollRef}
        >
            <OnlineTestOutro
                isTimeExpired={isAlreadySubmitted ? false : isTimeOver}
                skipCelebration={isAlreadySubmitted}
                loginCode={privateAccessMember?.loginCode.toUpperCase()}
                showCodeFirst={!!privateAccessMember && isCodeReminderDue(privateAccessMember.id)}
                onCodeNoted={clearCodeReminder}
                scrollContainerRef={outroScrollRef}
            />
        </OnlineTestLayout>
    );

    const renderLoading = () => (
        <div
            className="flex items-center justify-center w-full min-h-full"
            data-testid="online-test-loading"
            aria-label={t_loading}
        >
            <LoadingSpinner />
        </div>
    );

    const renderLoadError = () => (
        <div
            className="flex flex-col items-center justify-center gap-m w-full min-h-full p-l text-center"
            data-testid="online-test-class-fetch-error"
        >
            <PhaseSixIcon
                name="alert"
                className="text-[56px] text-p6-grey-dark"
            />
            <p className="text-l text-p6-grey-dark">{t_loadError}</p>
            <AreaButton
                variant={ButtonVariant.PRIMARY}
                role="button"
                onClick={retryLoad}
            >
                {t_retry}
            </AreaButton>
            {renderBackToClassroomLink()}
        </div>
    );

    const renderCurrentView = () => {
        switch (currentMainView) {
            case Views.INTRO:
                return renderIntroView();
            case Views.EXERCISES:
                return showOverview ? renderExercisesOverview() : renderExercisesView();
            case Views.OUTRO:
                return renderOutroView();
            case Views.UNAUTHORIZED:
                return <OnlineTestError />;
            default:
                return null;
        }
    };

    /*
     * No `default:` *and* an explicit return type: that pairing is what makes a phase added later a compile
     * error. Inferred, an unhandled case merely widens the return type to include `undefined`.
     */
    const renderMainContent = (): JSX.Element | null => {
        // Registering logs the pupil in and moves the machine past LOGIN_REQUIRED; the welcome screen outlasts that.
        if (isOnWelcome) return renderLoginRequiredView();

        switch (machineState.phase) {
            // CHECKING_ACCESS holds the spinner too, which is what stops the intro flashing "Start now"
            // at a member who already submitted, before the check lands.
            case OnlineTestPhases.LOADING_TEST:
            case OnlineTestPhases.LOADING_CLASS:
            case OnlineTestPhases.RESOLVING_IDENTITY:
            case OnlineTestPhases.CHECKING_ACCESS:
                return renderLoading();

            case OnlineTestPhases.LOAD_FAILED:
                // A test that is gone stays gone, so offering a retry would only fail again — that is the
                // 404 screen. Anything else is worth another attempt.
                return machineState.kind === LoadFailureKinds.TERMINAL ? <OnlineTestError /> : renderLoadError();

            case OnlineTestPhases.LOGIN_REQUIRED:
                return renderLoginRequiredView();

            case OnlineTestPhases.ALREADY_SUBMITTED:
                return renderOutroView(true);

            // The intro renders its own hidden/planned card. The OUTRO is exempt: a student who already
            // submitted keeps their confirmation screen even if the teacher hides the test afterwards.
            // (ALREADY_SUBMITTED covers that for login-gated tests, but a public-class student has no
            // result to look up.)
            case OnlineTestPhases.BLOCKED:
                return currentMainView === Views.OUTRO ? renderOutroView() : renderIntroView();

            case OnlineTestPhases.READY:
                // Views.INTRO is 0, so a negative view means no view has been chosen yet. `refreshData` sets
                // it in the same batch as the record, so this is belt-and-braces — kept because rendering
                // through it paints an empty frame: every view branch returns null.
                return currentMainView < 0 ? renderLoading() : renderCurrentView();
        }
    };

    return (
        <>
            {renderMainContent()}
            <ToastMessage
                text={t_submitError}
                isOpen={submitErrorToast.isOpen}
                onOpenChange={submitErrorToast.onOpenChange}
                variant={ToastVariants.ERROR}
            />
        </>
    );
};

export default OnlineTest;
